import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  isActivityBlockDone,
  computeEnthusiasmProgressPercent,
  ENTHUSIASM_BREAKDOWN_KEYS,
  ENTHUSIASM_DAILY_GOAL_POINTS,
} from '../../utils/enthusiasmDaily';

function sumBreakdownSubtotals(breakdown) {
  if (!breakdown || typeof breakdown !== 'object') return 0;
  return ENTHUSIASM_BREAKDOWN_KEYS.reduce((acc, k) => {
    const n = Number(breakdown[k]?.subtotal ?? 0);
    return acc + (Number.isFinite(n) && n > 0 ? n : 0);
  }, 0);
}

/**
 * شريط تقدم مسار الحماسة — تدرجات + لمعان متحرك + خطوات الأنشطة.
 * `compact`: داخل لوحة الحماسة (بدون هامش سفلي كبير).
 */
export default function EnthusiasmPathProgressBar({ breakdown, dailyData, compact = false }) {
  const { t } = useTranslation();

  const pctRounded = useMemo(() => {
    const rawTotal =
      dailyData?.total != null ? Number(dailyData.total) : sumBreakdownSubtotals(breakdown);
    const total = Number.isFinite(rawTotal) && rawTotal >= 0 ? rawTotal : 0;
    if (!dailyData && (!breakdown || typeof breakdown !== 'object')) return 0;
    if (dailyData) {
      return Math.round(Math.min(100, Math.max(0, computeEnthusiasmProgressPercent(total, dailyData))));
    }
    const goal = ENTHUSIASM_DAILY_GOAL_POINTS;
    return Math.min(100, Math.round((total / goal) * 1000) / 10);
  }, [breakdown, dailyData]);

  const stepDone = useMemo(
    () => ENTHUSIASM_BREAKDOWN_KEYS.map((k) => isActivityBlockDone(breakdown?.[k])),
    [breakdown]
  );

  const dotColors = [
    'from-cyan-400 to-sky-500',
    'from-violet-500 to-indigo-600',
    'from-rose-400 to-fuchsia-600',
    'from-amber-400 to-orange-500',
  ];

  const wrapCls = compact ? 'w-full max-w-full' : 'mb-6 sm:mb-8 max-w-3xl';
  const labelRowCls = compact ? 'mb-1 flex flex-wrap items-end justify-between gap-1.5' : 'mb-2 flex flex-wrap items-end justify-between gap-2';
  const labelCls = compact
    ? 'text-[10px] font-bold uppercase tracking-[0.1em] text-slate-500 sm:text-[11px]'
    : 'text-[11px] font-bold uppercase tracking-[0.12em] text-slate-500 sm:text-xs';
  const pctWrapCls = compact ? 'text-base font-black tabular-nums sm:text-lg' : 'text-xl font-black tabular-nums sm:text-2xl';
  const pctSuffixCls = compact ? 'text-xs font-bold text-slate-400 sm:text-sm' : 'text-base font-bold text-slate-400 sm:text-lg';
  const trackPadCls = compact ? 'p-[2px]' : 'p-[3px]';
  const trackHCls = compact ? 'h-2 sm:h-2.5' : 'h-3 sm:h-3.5';
  const dotsRowCls = compact ? 'mt-1.5 flex justify-between gap-1 sm:gap-1.5' : 'mt-3 flex justify-between gap-1 sm:gap-2';
  const dotHCls = compact ? 'h-1.5' : 'h-2';

  return (
    <div className={wrapCls}>
      <div className={labelRowCls}>
        <span className={labelCls}>
          {t('dashboard.student.enthusiasmPathProgressBar')}
        </span>
        <span className={pctWrapCls} aria-hidden>
          <span className="bg-gradient-to-r from-cyan-600 via-violet-600 to-amber-600 bg-clip-text text-transparent">
            {pctRounded}
          </span>
          <span className={pctSuffixCls}>%</span>
        </span>
      </div>

      <div className={`relative overflow-hidden rounded-full ${trackPadCls} shadow-inner ring-1 ring-slate-200/80`}>
        <div
          className="absolute inset-0 rounded-full bg-gradient-to-r from-cyan-200/40 via-violet-200/35 via-rose-200/35 to-amber-200/40"
          aria-hidden
        />
        <div
          className={`relative overflow-hidden rounded-full bg-slate-200/50 ${trackHCls}`}
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={pctRounded}
          aria-label={t('dashboard.student.enthusiasmPathProgressBar')}
        >
          <div
            className="absolute inset-y-0 start-0 overflow-hidden rounded-full shadow-[0_0_14px_rgba(99,102,241,0.35)] transition-[width] duration-700 [transition-timing-function:cubic-bezier(0.22,1,0.36,1)]"
            style={{ width: `${pctRounded}%` }}
          >
            <div
              className="absolute inset-0 bg-gradient-to-r from-cyan-500 via-violet-500 via-fuchsia-500 to-amber-500"
              aria-hidden
            />
            <div
              className="pointer-events-none absolute inset-y-0 w-[45%] bg-gradient-to-r from-transparent via-white/45 to-transparent opacity-90 animate-enthusiasm-path-shimmer"
              aria-hidden
            />
            <div
              className="pointer-events-none absolute inset-0 bg-[linear-gradient(180deg,rgba(255,255,255,0.35)_0%,transparent_45%,rgba(0,0,0,0.06)_100%)]"
              aria-hidden
            />
          </div>
        </div>
      </div>

      <div className={dotsRowCls} aria-hidden>
        {stepDone.map((done, i) => (
          <div
            key={i}
            className={`${dotHCls} flex-1 rounded-full bg-gradient-to-r ${dotColors[i]} opacity-25 transition-opacity duration-300 ${
              done ? 'opacity-100 shadow-sm ring-1 ring-white/60' : ''
            }`}
          />
        ))}
      </div>
    </div>
  );
}
